import { useEffect, useState } from "react";
import { FaPlusSquare } from "react-icons/fa";
import { AiOutlinePlus } from "react-icons/ai";
import RightTimeApi from "@/API";
import Enums from "@/enums";
import ContentEditModal from "@modals/ContentEditModal";

type Language = {
  id: number;
  abbrev?: string;
};

type ContentLanguage = {
  languageId: number;
};

type SystemContent = {
  id: number;
  name: string;
  contentTypeName: string;
  contentTypeId: number;
  languages: ContentLanguage[];
  format?: number;
};

type SystemContentPayload = {
  languages: Language[];
  contents: SystemContent[];
};

export default function AdminContentsTab(props: any) {
  const { activeTab, updateSpinner } = props.tabData;
  const [contentData, setContentData] = useState<SystemContentPayload | null>(null);
  const [sortedContents, setSortedContents] = useState<SystemContent[]>([]);
  const [isContentEditModalOpen, setIsContentEditModalOpen] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState(0);
  const [selectedContentFormat, setSelectedContentFormat] = useState<number>(Enums.CONTENT_FORMAT.HTML);

  useEffect(() => {
    if (!isOnThisTab()) return;
    void getContentDataAsync();
  }, [activeTab]);

  function isOnThisTab() {
    return activeTab === "contents";
  }

  async function getContentDataAsync() {
    if (!isOnThisTab()) return;

    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync("/CompanyContent/System");
      if (resp?.ok && resp.jsonData) {
        const payload = resp.jsonData as SystemContentPayload;
        setContentData(payload);
        const sorted = [...(payload.contents || [])].sort((a, b) => {
          if (a.contentTypeId !== b.contentTypeId) return a.contentTypeId - b.contentTypeId;
          return (a.name || "").localeCompare(b.name || "");
        });
        setSortedContents(sorted);
      } else {
        setContentData({ languages: [], contents: [] });
        setSortedContents([]);
      }
    } finally {
      updateSpinner?.(false);
    }
  }

  function onAddContent() {
    setSelectedContentId(0);
    setIsContentEditModalOpen(true);
  }

  async function onEditModalCloseAsync() {
    setIsContentEditModalOpen(false);
    setSelectedContentId(0);
    await getContentDataAsync();
  }

  if (!isOnThisTab()) return null;

  return (
    <>
      <div className="tableWrapper dimensionTable systemContentTable">
        <table>
          <caption data-help-id="SystemContents">
            <span className="title">System Contents</span>
            <span
              className="actionIcon plusIcon floatRight contentAddAction"
              onClick={onAddContent}
              title="Add System Content"
            >
              <FaPlusSquare />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Id</th>
              <th>Type</th>
              <th>Name</th>
              <th>Languages</th>
                         <th>Format</th>
            </tr>
          </thead>
          <tbody>
            {sortedContents.map((content) => {
              const langAbbrList = (content.languages || [])
                .map((l) => contentData?.languages?.find((lang) => lang.id === l.languageId)?.abbrev || "")
                .filter(Boolean)
                .join(", ");

              return (
                <tr
                  key={content.id}
                  onClick={() => {
                    setSelectedContentId(content.id);
                    setSelectedContentFormat(content.format ?? Enums.CONTENT_FORMAT.HTML);
                    setIsContentEditModalOpen(true);
                  }}
                  className="clickableRow"
                >
                  <td>{content.id}</td>
                  <td>{content.contentTypeName}</td>
                  <td>{content.name}</td>
                  <td className="center clickable-cell" title={langAbbrList ? `Edit content for: ${langAbbrList}` : "Add content"}>
                    {langAbbrList || <AiOutlinePlus className="actionIcon plusIcon" />}
                  </td>
                  <td>{content.format === Enums.CONTENT_FORMAT.Text ? "Text" : content.format === Enums.CONTENT_FORMAT.JSON ? "JSON" : "HTML"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ContentEditModal
        isOpen={isContentEditModalOpen}
        onClose={onEditModalCloseAsync}
        companyId={0}
        companyContentId={selectedContentId}
      />
    </>
  );
}
